import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'

/**
 * Views are lazily imported so the initial bundle stays small; the trackers
 * pull in tables and filters that a first-time visitor to the dashboard does
 * not need.
 */
const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'dashboard',
    component: () => import('@/views/DashboardView.vue'),
    meta: { title: 'Dashboard' },
  },
  {
    path: '/clients',
    name: 'clients',
    component: () => import('@/views/ClientsView.vue'),
    meta: { title: 'Clients' },
  },
  {
    path: '/clients/:id',
    name: 'client-detail',
    component: () => import('@/views/ClientDetailView.vue'),
    props: true,
    meta: { title: 'Client' },
  },
  {
    path: '/projects',
    name: 'projects',
    component: () => import('@/views/ProjectsView.vue'),
    meta: { title: 'Projects' },
  },
  {
    path: '/projects/:id',
    name: 'project-detail',
    component: () => import('@/views/ProjectDetailView.vue'),
    props: true,
    meta: { title: 'Project' },
  },
  {
    // A project's calendar as printable pages. Bare: no masthead or page
    // padding, so the screen shows exactly what prints.
    path: '/projects/:id/calendar/print',
    name: 'project-calendar-print',
    component: () => import('@/views/ProjectCalendarPrintView.vue'),
    props: true,
    meta: { title: 'Print calendar', bare: true },
  },
  {
    path: '/deliverables',
    name: 'deliverables',
    component: () => import('@/views/DeliverablesView.vue'),
    meta: { title: 'Delivery' },
  },
  {
    path: '/deliverables/:id',
    name: 'deliverable-detail',
    component: () => import('@/views/DeliverableDetailView.vue'),
    props: true,
    meta: { title: 'Deliverable' },
  },
  {
    path: '/languages',
    name: 'languages',
    component: () => import('@/views/LanguageVersionsView.vue'),
    meta: { title: 'Languages' },
  },
  {
    path: '/casting',
    name: 'casting',
    component: () => import('@/views/CastingView.vue'),
    meta: { title: 'Casting' },
  },
  {
    path: '/outstanding',
    name: 'outstanding',
    component: () => import('@/views/OutstandingView.vue'),
    meta: { title: 'Still needed' },
  },
  {
    path: '/intake/:batchId?',
    name: 'intake',
    component: () => import('@/views/IntakeView.vue'),
    props: true,
    meta: { editorOnly: true, title: 'Intake' },
  },
  {
    path: '/users',
    name: 'users',
    component: () => import('@/views/UsersView.vue'),
    meta: { adminOnly: true, title: 'Users' },
  },
  {
    // Adobe's registered redirect target. Must stay at this exact path or the
    // OAuth app's redirect URI stops matching.
    path: '/oauth/callback',
    name: 'oauth-callback',
    component: () => import('@/views/OAuthCallbackView.vue'),
    meta: { adminOnly: true, title: 'Connecting Frame.io' },
  },
  {
    path: '/vendors-markets',
    name: 'vendors-markets',
    component: () => import('@/views/VendorsMarketsView.vue'),
    meta: { adminOnly: true, title: 'Vendors & markets' },
  },
  {
    // Adobe's OAuth callback page links back here, so this path should stay.
    path: '/settings',
    name: 'settings',
    component: () => import('@/views/SettingsView.vue'),
    meta: { adminOnly: true, title: 'Settings' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { title: 'Not found' },
  },
]

declare module 'vue-router' {
  interface RouteMeta {
    title?: string
    /**
     * Admin-only page. Enforced where the page renders (App.vue), not in a
     * navigation guard: the role arrives from the API after Clerk resolves,
     * which is after the first navigation, so a guard would have to either
     * block on it or guess.
     */
    adminOnly?: boolean
    /** For whoever may edit project work: admins and producers. */
    editorOnly?: boolean
    /** Rendered without the masthead and page chrome -- print views. */
    bare?: boolean
  }
}

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: (_to, _from, saved) => saved ?? { top: 0 },
})

const APP_NAME = import.meta.env.VITE_APP_NAME ?? 'Bedouin'

router.afterEach((to) => {
  const title = to.meta.title as string | undefined
  document.title = title ? `${title} - ${APP_NAME}` : APP_NAME
})
